// src/components/Admin/FirmPortalFeed.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Box, Card, CardContent, Typography, CircularProgress, Alert, Paper,
    Grid, FormControl, InputLabel, Select, MenuItem, Chip, Collapse,
    IconButton, Divider, Stack, Avatar, Tooltip, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Rating,
    FormControlLabel, Checkbox
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    PersonOutline as PersonOutlineIcon,
    SmartToyOutlined as SmartToyIcon,
    Article as ArticleIcon,
    CalendarToday as CalendarIcon,
    Chat as ChatIcon,
    Share as ShareIcon,
    FileDownload as FileDownloadIcon,
    EditNote as EditNoteIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Feedback as FeedbackIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    AdminPanelSettings as AdminIcon,
    Send as SendIcon
} from '@mui/icons-material';
import { collection, query, where, onSnapshot, orderBy, getDocs, doc, getDoc, updateDoc, limit } from 'firebase/firestore';
import { serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType } from 'docx';
import ShareModal from '../ShareModal';
import BEPMemoDisplay from '../DecisionMemo/BEPMemoDisplay'; // NEW: Import BEP component

const SearchInput = React.memo(({ onSearch, onClear, initialValue = '' }) => {
    const [inputValue, setInputValue] = React.useState(initialValue);
    
    // Sync local state if the prop changes (e.g. from URL deep link)
    useEffect(() => {
        setInputValue(initialValue);
    }, [initialValue]);
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onSearch(inputValue);
        }
    };
    
    const handleClear = () => {
        setInputValue('');
        onClear();
    };

    const handleSearchClick = () => {
        onSearch(inputValue);
    };
    
    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
                placeholder="Search and press Enter..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                fullWidth
                InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    endAdornment: inputValue && (
                        <IconButton size="small" onClick={handleClear}>
                            <ClearIcon />
                        </IconButton>
                    )
                }}
            />
            <Button
                variant="contained"
                onClick={handleSearchClick}
                size="small"
                disabled={!inputValue.trim()}
                sx={{ height: '40px', minWidth: '80px' }}
            >
                Search
            </Button>
        </Box>
    );
});

const FirmPortalFeed = ({ user, orgConfig, configLoading, initialSearchQuery = '' }) => {
    const [organization, setOrganization] = useState(null);
    const [members, setMembers] = useState([]);
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLoadingFullHistory, setIsLoadingFullHistory] = useState(false);
    const [showLoadingNote, setShowLoadingNote] = useState(false);
    // Time-boxed default: 90 days
    const DEFAULT_DAYS_BACK = 90;
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [error, setError] = useState('');
    
    // Search state
    const [localSearchQuery, setLocalSearchQuery] = useState(initialSearchQuery);
    
    // When initialSearchQuery changes (e.g., from URL deep link), apply it
    useEffect(() => {
        if (initialSearchQuery) {
            setLocalSearchQuery(initialSearchQuery);
        }
    }, [initialSearchQuery]);
    
    // Date range state
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [expandedSources, setExpandedSources] = useState(new Set());
    const [selectedFcfoId, setSelectedFcfoId] = useState('ALL');
    const [selectedClientId, setSelectedClientId] = useState('ALL');
    const [sortOption, setSortOption] = useState('newest');
    
    // State for Notes and Share Modals
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [noteText, setNoteText] = useState('');
    
    // State for Feedback
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [selectedItemForFeedback, setSelectedItemForFeedback] = useState(null);
    const [feedbackRating, setFeedbackRating] = useState(0);
    const [feedbackText, setFeedbackText] = useState('');
    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    
    // State for Export
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFormat, setExportFormat] = useState('csv');
    
    // Follow-up state
    const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
    const followUpRef = useRef(null);
    const [followUpSubmitting, setFollowUpSubmitting] = useState(false);
    const [followUpParent, setFollowUpParent] = useState(null); // { id, type, clientId, item }
    
    // Decision Memo state
    const [memoGenerating, setMemoGenerating] = useState(null); // ID of item generating memo
    
    const [exportFields, setExportFields] = useState({
        itemId: true,
        fcfoName: true,
        clientName: true,
        dateCreated: true,
        questionText: true,
        answerText: true,
        userNotes: true,
        orgAdminNote: true,
        includeFollowUps: true,
        bu_title: true,
        bu_content: true,
        bu_sources: true,
    });
    
    // Check if user is admin
    const isAdmin = user?.firmPortalRole === 'admin';
    
    // Fetch organization data and members
    useEffect(() => {
        const fetchOrgData = async () => {
            if (!user?.organizationId) {
                setError("Your user account is not associated with a firm.");
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                // 1. Fetch organization document
                const orgRef = doc(db, 'organizations', user.organizationId);
                const orgSnap = await getDoc(orgRef);
                if (!orgSnap.exists()) {
                    throw new Error("Could not find your firm's organization record.");
                }
                const orgData = { id: orgSnap.id, ...orgSnap.data() };
                setOrganization(orgData);

                 // 2. Fetch all members (users) of the organization
                const membersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', user.organizationId)
                );
                const membersSnap = await getDocs(membersQuery);
                const memberList = membersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Filter to only show users with User Portal access in the FCFO dropdown
                // userPortalAccess: true or undefined = has access (default for legacy users)
                // userPortalAccess: false = firm-only admin, exclude from dropdown
                const filteredMembers = memberList.filter(member => 
                    member.userPortalAccess !== false
                );

                setMembers(filteredMembers);


                // 3. Fetch all clients for the organization
                const clientsQuery = query(
                    collection(db, 'clients'),
                    where('organizationId', '==', user.organizationId)
                );
                const clientsSnap = await getDocs(clientsQuery);
                const clientList = clientsSnap.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data() 
                }));
                setClients(clientList);

                setLoading(false);
            } catch (err) {
                console.error("Error fetching firm data:", err);
                setError(`Failed to load firm data: ${err.message}`);
                setLoading(false);
            }
        };

        fetchOrgData();
    }, [user]);

    // ============================================================================
// TWO-WAVE LOADING: Fast initial load + background full history
// ============================================================================
const INITIAL_BATCH_SIZE = 50;
const LOADING_NOTE_DELAY_MS = 3000;

useEffect(() => {
    if (!user?.organizationId || loading) return;

    setIsLoadingFullHistory(false);
    setShowLoadingNote(false);

    // Timer reference for cleanup
    let loadingNoteTimer = null;
    let unsubQ = null;
    let unsubU = null;

    const questionsRef = collection(db, 'questions');
    const updatesRef = collection(db, 'businessUpdates');

    // Calculate 90-day boundary for default view
    const getDefaultDateBoundary = () => {
        const date = new Date();
        date.setDate(date.getDate() - DEFAULT_DAYS_BACK);
        return date;
    };

    // Build base queries - apply 90-day filter unless showAllHistory is true

    // Build base queries - apply 90-day filter unless showAllHistory is true
    const dateBoundary = showAllHistory ? null : getDefaultDateBoundary();

    const baseQQuery = dateBoundary
        ? query(
            questionsRef,
            where('organizationId', '==', user.organizationId),
            where('createdAt', '>=', dateBoundary),
            orderBy('createdAt', 'desc')
        )
        : query(
            questionsRef,
            where('organizationId', '==', user.organizationId),
            orderBy('createdAt', 'desc')
        );

    const baseUQuery = dateBoundary
        ? query(
            updatesRef,
            where('organizationId', '==', user.organizationId),
            where('createdAt', '>=', dateBoundary),
            orderBy('createdAt', 'desc')
        )
        : query(
            updatesRef,
            where('organizationId', '==', user.organizationId),
            orderBy('createdAt', 'desc')
        );

    // Build limited queries for fast initial load
    const initialQQuery = query(baseQQuery, limit(INITIAL_BATCH_SIZE));
    const initialUQuery = query(baseUQuery, limit(INITIAL_BATCH_SIZE));

    // Track load states
    let initialLoadComplete = false;
    let fullLoadComplete = false;

    // STEP 1: Fast initial load with limit
    Promise.all([
        getDocs(initialQQuery),
        getDocs(initialUQuery)
    ]).then(([questionsSnap, updatesSnap]) => {
        const initialQuestions = questionsSnap.docs.map(d => ({ 
            ...d.data(), 
            id: d.id, 
            dataType: 'question' 
        }));
        const initialUpdates = updatesSnap.docs.map(d => ({ 
            ...d.data(), 
            id: d.id, 
            dataType: 'update' 
        }));

        const combined = [...initialQuestions, ...initialUpdates].sort(
            (a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
        );

        // Only set if onSnapshot hasn't already delivered full data
        if (!initialLoadComplete && !fullLoadComplete) {
            console.log(`[PERF] Firm Feed - Initial load complete: ${combined.length} items (${initialQuestions.length} questions, ${initialUpdates.length} updates)`);
            setItems(combined);

            // If we got less than the limit, we have all the data already
            const hasMoreData = questionsSnap.size >= INITIAL_BATCH_SIZE || updatesSnap.size >= INITIAL_BATCH_SIZE;
            if (hasMoreData) {
                setIsLoadingFullHistory(true);
                // Show loading note only if background load takes > 3 seconds
                loadingNoteTimer = setTimeout(() => {
                    if (!fullLoadComplete) {
                        setShowLoadingNote(true);
                    }
                }, LOADING_NOTE_DELAY_MS);
            }
            initialLoadComplete = true;
        }
    }).catch(err => {
        console.error('[ERROR] Firm Feed - Initial load failed:', err);
        // Don't set error - onSnapshot will still try to load
    });

    // STEP 2: Full history via onSnapshot (runs in parallel, delivers when ready)
    let latestQuestions = null;
    let latestUpdates = null;

    const maybeCommit = () => {
        if (latestQuestions === null || latestUpdates === null) return;

        fullLoadComplete = true; // Prevent initial load from overwriting

        // Clear the timer if it hasn't fired yet
        if (loadingNoteTimer) {
            clearTimeout(loadingNoteTimer);
            loadingNoteTimer = null;
        }

        const combined = [...latestQuestions, ...latestUpdates].sort(
            (a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
        );

        console.log(`[PERF] Firm Feed - Full history loaded: ${combined.length} items (${latestQuestions.length} questions, ${latestUpdates.length} updates)`);
        setItems(combined);
        setIsLoadingFullHistory(false);
        setShowLoadingNote(false);
    };

    unsubQ = onSnapshot(
        baseQQuery,
        snap => {
            latestQuestions = snap.docs.map(d => ({ 
                ...d.data(), 
                id: d.id, 
                dataType: 'question' 
            }));
            maybeCommit();
        },
        err => {
            console.error('[ERROR] Firm Feed - questions snapshot:', err);
            setIsLoadingFullHistory(false);
            setShowLoadingNote(false);
        }
    );

    unsubU = onSnapshot(
        baseUQuery,
        snap => {
            latestUpdates = snap.docs.map(d => ({ 
                ...d.data(), 
                id: d.id, 
                dataType: 'update' 
            }));
            maybeCommit();
        },
        err => {
            console.error('[ERROR] Firm Feed - updates snapshot:', err);
            setIsLoadingFullHistory(false);
            setShowLoadingNote(false);
        }
    );

    return () => {
        // Cleanup timer on unmount
        if (loadingNoteTimer) {
            clearTimeout(loadingNoteTimer);
        }
        if (unsubQ) unsubQ();
        if (unsubU) unsubU();
    };
}, [user?.organizationId, loading, showAllHistory]);

    // Memoized list of clients for the dropdown, filtered by selected FCFO
    const filteredClients = useMemo(() => {
        if (selectedFcfoId === 'ALL') {
            return clients;
        }
        return clients.filter(c => c.fcfoId === selectedFcfoId);
    }, [clients, selectedFcfoId]);

    // Reset client filter if selected FCFO changes
    useEffect(() => {
        if (selectedClientId !== 'ALL' && !filteredClients.some(c => c.id === selectedClientId)) {
            setSelectedClientId('ALL');
        }
    }, [selectedFcfoId, filteredClients, selectedClientId]);

    // Reset to 90-day default view when FCFO or Client filter changes
    useEffect(() => {
        setShowAllHistory(false);
    }, [selectedFcfoId, selectedClientId]);

    // Process items for display (apply filters, thread follow-ups)
    const processedItems = useMemo(() => {
        let filtered = [...items];

        // Apply FCFO filter
        if (selectedFcfoId !== 'ALL') {
            filtered = filtered.filter(item => item.fcfoId === selectedFcfoId);
        }

        // Apply Client filter
        if (selectedClientId !== 'ALL') {
            filtered = filtered.filter(item => item.clientId === selectedClientId);
        }
        
        // Apply date range filter
        if (dateFrom || dateTo) {
            const fromDate = dateFrom ? new Date(dateFrom) : null;
            const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;
            filtered = filtered.filter(item => {
                if (!item.createdAt?.toDate) return false;
                const itemDate = item.createdAt.toDate();
                if (fromDate && itemDate < fromDate) return false;
                if (toDate && itemDate > toDate) return false;
                return true;
            });
        }

        // Apply search filter
        if (localSearchQuery.trim()) {
            const searchTerm = localSearchQuery.toLowerCase();
            filtered = filtered.filter(item => {
                if (item.dataType === 'question') {
                    const mainMatches =
                        item.id?.toLowerCase().includes(searchTerm) ||
                        item.questionText?.toLowerCase().includes(searchTerm) ||
                        item.answer?.toLowerCase().includes(searchTerm) ||
                        item.clientName?.toLowerCase().includes(searchTerm) ||
                        item.fcfoName?.toLowerCase().includes(searchTerm) ||
                        item.userNotes?.toLowerCase().includes(searchTerm) ||
                        item.orgAdminNote?.toLowerCase().includes(searchTerm);

                    const followUpMatches = (item.followUps || []).some(followUp =>
                        followUp.questionText?.toLowerCase().includes(searchTerm) ||
                        followUp.answer?.toLowerCase().includes(searchTerm)
                    );
                    return mainMatches || followUpMatches;
                }
                if (item.dataType === 'update') {
                    const mainMatches =
                        item.id?.toLowerCase().includes(searchTerm) ||
                        item.title?.toLowerCase().includes(searchTerm) ||
                        item.content?.toLowerCase().includes(searchTerm) ||
                        item.clientName?.toLowerCase().includes(searchTerm) ||
                        item.fcfoName?.toLowerCase().includes(searchTerm) ||
                        item.userNotes?.toLowerCase().includes(searchTerm) ||
                        item.orgAdminNote?.toLowerCase().includes(searchTerm);

                    const followUpMatches = (item.followUps || []).some(followUp =>
                        followUp.questionText?.toLowerCase().includes(searchTerm) ||
                        followUp.answer?.toLowerCase().includes(searchTerm)
                    );
                    return mainMatches || followUpMatches;
                }
                return false;
            });
        }
        
        // Sorting Logic
        switch (sortOption) {
            case 'oldest':
                filtered.sort((a, b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0));
                break;
            case 'newest':
            default:
                filtered.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
                break;
        }

        // Separate follow-ups and main items
        const followUps = filtered.filter(item => 
            item.dataType === 'question' && item.parentId
        );

        const questionFollowUpMap = {};
        const businessUpdateFollowUpMap = {};

        followUps.forEach(followUp => {
            if (followUp.parentType === 'business_update') {
                if (!businessUpdateFollowUpMap[followUp.parentId]) {
                    businessUpdateFollowUpMap[followUp.parentId] = [];
                }
                businessUpdateFollowUpMap[followUp.parentId].push(followUp);
            } else {
                if (!questionFollowUpMap[followUp.parentId]) {
                    questionFollowUpMap[followUp.parentId] = [];
                }
                questionFollowUpMap[followUp.parentId].push(followUp);
            }
        });

        // Sort follow-ups by creation date
        Object.keys(questionFollowUpMap).forEach(parentId => {
            questionFollowUpMap[parentId].sort((a, b) =>
                (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0)
            );
        });

        Object.keys(businessUpdateFollowUpMap).forEach(parentId => {
            businessUpdateFollowUpMap[parentId].sort((a, b) =>
                (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0)
            );
        });

        // Attach follow-ups to parent items and add metadata
let itemsWithFollowUps = filtered.map(item => {
    const client = clients.find(c => c.id === item.clientId);
    const member = members.find(m => m.id === item.fcfoId);

    const enrichedItem = {
        ...item,
        clientName: client?.name || 'Unknown Client',
        fcfoName: member?.name || member?.email || 'Unknown Advisor'
    };

    if (item.dataType === 'question' && !item.parentId) {
        // MERGE embedded followUps with mapped follow-ups
        const embeddedFollowUps = item.followUps || [];
        const mappedFollowUps = questionFollowUpMap[item.id] || [];
        const allFollowUps = [...embeddedFollowUps, ...mappedFollowUps];
        
        // Sort by creation date
        allFollowUps.sort((a, b) => {
            const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime();
            const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime();
            return aTime - bTime;
        });
        
        enrichedItem.followUps = allFollowUps;
    } else if (item.dataType === 'update') {
        // Business Updates: MERGE embedded followUps with mapped follow-ups
        const embeddedFollowUps = item.followUps || [];
        const mappedFollowUps = businessUpdateFollowUpMap[item.id] || [];
        const allFollowUps = [...embeddedFollowUps, ...mappedFollowUps];
        
        allFollowUps.sort((a, b) => {
            const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime();
            const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime();
            return aTime - bTime;
        });
        
        enrichedItem.followUps = allFollowUps;
    }

    return enrichedItem;
}).filter(item => !(item.dataType === 'question' && item.parentId));

         return itemsWithFollowUps;
    }, [items, selectedFcfoId, selectedClientId, sortOption, clients, members, localSearchQuery, dateFrom, dateTo]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp?.toDate) return 'Unknown date';
        const date = timestamp.toDate();
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Helper function to highlight search terms
const highlightSearchTerms = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(re);

    return parts.map((part, i) =>
        i % 2 === 1
            ? <span key={i} style={{ backgroundColor: '#ffeb3b', fontWeight: 'bold' }}>{part}</span>
            : <span key={i}>{part}</span>
    );
};

// Helper to process React children and apply highlighting to text nodes
const processTextChildren = (children, searchTerm) => {
    if (!searchTerm) return children;
    return React.Children.map(children, child => {
        if (typeof child === 'string') {
            return highlightSearchTerms(child, searchTerm);
        }
        return child;
    });
};

// Custom ReactMarkdown components that apply search highlighting
const getHighlightedMarkdownComponents = (searchTerm) => {
    if (!searchTerm?.trim()) return {};
    
    return {
        p: ({children, ...props}) => <p {...props}>{processTextChildren(children, searchTerm)}</p>,
        li: ({children, ...props}) => <li {...props}>{processTextChildren(children, searchTerm)}</li>,
        td: ({children, ...props}) => <td {...props}>{processTextChildren(children, searchTerm)}</td>,
        th: ({children, ...props}) => <th {...props}>{processTextChildren(children, searchTerm)}</th>,
        strong: ({children, ...props}) => <strong {...props}>{processTextChildren(children, searchTerm)}</strong>,
        em: ({children, ...props}) => <em {...props}>{processTextChildren(children, searchTerm)}</em>,
    };
};

    // Toggle expansion
    const toggleExpansion = (itemId) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    // Toggle sources expansion
    const toggleSourcesExpansion = (itemId) => {
        setExpandedSources(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

     // Render follow-ups
    const renderFollowUps = (followUps) => {
        if (!followUps || followUps.length === 0) return null;

        return (
            <Box sx={{ ml: 1, mt: 2, borderLeft: '3px solid #f0f0f0', pl: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                    <ChatIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                    Follow-up Thread
                </Typography>
                <Stack spacing={2.5}>
                    {followUps.map((followUp) => {
                        // Check if this is a BEP Decision Memo
                        if (followUp.type === 'decision_memo' && followUp.framework === 'business_evolution_pyramid') {
                            return (
                                <Box key={followUp.id} sx={{ mt: 3, mb: 3 }}>
                                    <Paper elevation={2} sx={{ p: 2, bgcolor: '#f8f9fa', borderLeft: '4px solid #005DAA' }}>
                                        <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: '#005DAA', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ArticleIcon fontSize="small" />
                                            Decision Memo (Business Evolution Pyramid)
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                            Generated: {formatTimestamp(followUp.createdAt)}
                                        </Typography>
                                    </Paper>
                                    <BEPMemoDisplay followUpData={followUp} searchQuery={localSearchQuery} />
                                </Box>
                            );
                        }
                        
                        // Check if this is a standard Decision Memo
                        if (followUp.type === 'decision_memo') {
                            return (
                                <Box key={followUp.id} sx={{ mt: 3, mb: 3 }}>
                                    <Paper elevation={2} sx={{ p: 2, bgcolor: '#f8f9fa', borderLeft: '4px solid #1976d2' }}>
                                        <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ArticleIcon fontSize="small" />
                                            Decision Memo
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                            Generated: {formatTimestamp(followUp.createdAt)}
                                        </Typography>
                                    </Paper>
                                    <Paper elevation={1} sx={{ p: 3, mt: 2 }}>
                                        <Box sx={{ 
                                            fontSize: '1rem', 
                                            lineHeight: 1.7,
                                            '& h1, & h2, & h3': { mt: 2, mb: 1, fontWeight: 600 },
                                            '& p': { mb: 1.5 },
                                            '& ul, & ol': { pl: 3, mb: 2 },
                                            '& li': { mb: 0.5 },
                                            '& strong': { fontWeight: 600 },
                                            '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                                            '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
                                            '& th': { backgroundColor: '#f2f2f2', fontWeight: 600 }
                                        }}>
                                            <ReactMarkdown 
    remarkPlugins={[remarkGfm]}
    components={getHighlightedMarkdownComponents(localSearchQuery)}
>
    {followUp.response || followUp.answer}
</ReactMarkdown>
                                        </Box>
                                    </Paper>
                                </Box>
                            );
                        }
                        
                        // Regular Q&A follow-up rendering
                        return (
                            <Box key={followUp.id}>
                                {/* User's Follow-up Question */}
                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                    <Paper elevation={1} sx={{ p: 1.5, bgcolor: '#f5f5f5', maxWidth: '80%', borderRadius: '16px 16px 0 16px' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {followUp.questionText}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                                            {formatTimestamp(followUp.createdAt)}
                                        </Typography>
                                    </Paper>
                                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                                        <PersonOutlineIcon />
                                    </Avatar>
                                </Stack>

                                {/* AI's Answer */}
                                {followUp.answer && (
                                    <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                            <SmartToyIcon />
                                        </Avatar>
                                        <Paper elevation={2} sx={{ p: 1.5, bgcolor: 'white', maxWidth: '80%', borderRadius: '0 16px 16px 16px', border: '1px solid #e0e0e0' }}>
                                            <Box sx={{ 
                                                fontSize: '0.875rem', 
                                                lineHeight: 1.7,
                                                '& p': { margin: 0, marginBottom: '0.5em' },
                                                '& p:last-child': { marginBottom: 0 },
                                                '& ul, & ol': { marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '1.5em' },
                                                '& li': { marginBottom: '0.25em' },
                                                '& strong': { fontWeight: 600 },
                                                '& table': { width: '100%', borderCollapse: 'collapse', marginTop: '0.5em', marginBottom: '0.5em' },
                                                '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
                                                '& th': { backgroundColor: '#f2f2f2', fontWeight: 600 }
                                            }}>
                                                <ReactMarkdown 
    remarkPlugins={[remarkGfm]}
    components={getHighlightedMarkdownComponents(localSearchQuery)}
>
    {followUp.answer}
</ReactMarkdown>
                                            </Box>
                                        </Paper>
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Box>
        );
    };

    // Render question card
    const handleOpenNoteModal = (item) => {
        setSelectedItem(item);
        setNoteText(item.orgAdminNote || '');
        setNoteModalOpen(true);
    };
    
    const handleSaveNote = async () => {
        if (!selectedItem) return;
    
        const collectionName = selectedItem.dataType === 'question' ? 'questions' : 'businessUpdates';
        const docRef = doc(db, collectionName, selectedItem.id);
    
        try {
            await updateDoc(docRef, {
                orgAdminNote: noteText,
                orgAdminNoteLastUpdated: serverTimestamp(),
            });
            toast.success('Note saved successfully!');
            setNoteModalOpen(false);
            // Manually update the item in the local state to reflect the change instantly
            setItems(prevItems => prevItems.map(item =>
                item.id === selectedItem.id ? { ...item, orgAdminNote: noteText } : item
            ));
        } catch (error) {
            toast.error('Failed to save note.');
            console.error("Error saving note: ", error);
        }
    };
    
    const handleOpenShareModal = (item) => {
        setSelectedItem(item);
        setShareModalOpen(true);
    };

    // Handler for share action (called by ShareModal)
    const handleShare = async (shareData) => {
        if (!selectedItem) {
            throw new Error("No item selected for sharing.");
        }
        try {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const shareFirmPortalContentFn = httpsCallable(functions, 'shareFirmPortalContent');

            const payload = {
                itemType: selectedItem.dataType,
                item: {
                    clientName: selectedItem.clientName,
                    questionText: selectedItem.questionText,
                    answer: selectedItem.answer,
                    title: selectedItem.title,
                    content: selectedItem.content,
                    sources: selectedItem.sources,
                    followUps: selectedItem.followUps || []
                },
                recipientEmail: shareData.recipientEmail,
                recipientName: shareData.recipientName,
                personalMessage: shareData.customMessage || shareData.personalMessage || '',
                senderName: user?.displayName || user?.name || user?.email || 'Org Admin'
            };

            const result = await shareFirmPortalContentFn(payload);

            if (result.data.success) {
                console.log('Content shared successfully');
                return result.data;
            } else {
                throw new Error(result.data.error || 'Failed to share content.');
            }
        } catch (error) {
            console.error('Error calling shareFirmPortalContent function:', error);
            throw error; 
        }
    };

    // Open feedback dialog
    const openFeedbackDialog = (item) => {
        setSelectedItemForFeedback(item);
        // Pre-populate with existing feedback if available
        if (item.orgAdminFeedback) {
            setFeedbackRating(item.orgAdminFeedback.rating || 0);
            setFeedbackText(item.orgAdminFeedback.comment || '');
        } else {
            setFeedbackRating(0);
            setFeedbackText('');
        }
        setFeedbackDialogOpen(true);
    };

    // Close feedback dialog
    const closeFeedbackDialog = () => {
        setFeedbackDialogOpen(false);
        setFeedbackRating(0);
        setFeedbackText('');
        setSelectedItemForFeedback(null);
    };

    // Handle feedback submission
    const handleFeedbackSubmit = async () => {
        if (!selectedItemForFeedback || feedbackRating === 0) return;

        const userId = user?.uid || user?.id;
        if (!userId) {
            toast.error('User not authenticated');
            return;
        }

        setSubmittingFeedback(true);

        try {
            const collectionName = selectedItemForFeedback.dataType === 'update' ? 'businessUpdates' : 'questions';
            const docId = selectedItemForFeedback.id;
            const itemRef = doc(db, collectionName, docId);

            const feedbackData = {
                rating: feedbackRating,
                comment: feedbackText.trim(),
                submittedAt: serverTimestamp(),
                submittedBy: user.email
            };

            await updateDoc(itemRef, {
                orgAdminFeedback: feedbackData,
                updatedAt: serverTimestamp()
            });

            toast.success('Feedback submitted successfully!');
            closeFeedbackDialog();

            // Update local state to reflect the change
            setItems(prevItems => prevItems.map(item =>
                item.id === selectedItemForFeedback.id 
                    ? { ...item, orgAdminFeedback: feedbackData } 
                    : item
            ));
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Failed to submit feedback');
        } finally {
            setSubmittingFeedback(false);
        }
    };

    // ==================== FOLLOW-UP & DECISION MEMO FUNCTIONS ====================
    
    // Handle opening follow-up modal
    const handleOpenFollowUp = (item, type) => {
        setFollowUpParent({ id: item.id, type, clientId: item.clientId, item });
        if (followUpRef.current) followUpRef.current.value = '';
        setFollowUpModalOpen(true);
    };

    // Handle submitting follow-up
    const handleSubmitFollowUp = async () => {
        const text = (followUpRef.current?.value || '').trim();
        if (!text || !followUpParent) return;

        setFollowUpSubmitting(true);
        try {
            const functionName = followUpParent.type === 'question' 
                ? 'firmPortalFollowUpQuestion' 
                : 'firmPortalFollowUpBusinessUpdate';
            const callable = httpsCallable(functions, functionName, { timeout: 600000 });
            
            const params = followUpParent.type === 'question'
                ? { parentQuestionId: followUpParent.id, followUpText: text, clientId: followUpParent.clientId }
                : { businessUpdateId: followUpParent.id, followUpText: text, clientId: followUpParent.clientId };

            const result = await callable(params);

            if (result.data.success) {
                toast.success('Follow-up question submitted and answered!');
                setFollowUpModalOpen(false);
                if (followUpRef.current) followUpRef.current.value = '';
                setFollowUpParent(null);
            }
        } catch (error) {
            console.error('Error submitting follow-up:', error);
            toast.error(error.message || 'Failed to submit follow-up question');
        } finally {
            setFollowUpSubmitting(false);
        }
    };

     // Handle generating decision memo
    const handleGenerateDecisionMemo = async (item, parentType, signalNumber = null, trackingId = null) => {
        const itemId = item.id;
        const generatingId = trackingId || itemId;
        setMemoGenerating(generatingId);
        
        try {
            const generateDecisionMemo = httpsCallable(functions, 'generateDecisionMemo', { timeout: 600000 });
            const contextText = parentType === 'question' 
                ? item.questionText 
                : (item.title || 'SavvaiSignals');
                
            const result = await generateDecisionMemo({
                clientId: item.clientId,
                parentId: itemId,
                parentType: parentType,
                contextText: contextText,
                signalNumber: signalNumber
            });

            if (result.data.success) {
                toast.success('Decision Memo generated successfully!');
            }
        } catch (error) {
            console.error('Error generating Decision Memo:', error);
            toast.error(error.message || 'Failed to generate Decision Memo');
        } finally {
            setMemoGenerating(null);
        }
    };
    
    // ==================== EXPORT FUNCTIONS ====================
    
    // Generate CSV content with proper follow-up handling
    const generateCSVContent = (itemsToExport, fields) => {
        const fieldMap = {
            itemId: 'ID',
            fcfoName: 'Advisor Name',
            clientName: 'Client Name',
            dateCreated: 'Date Created',
            questionText: 'Question Text',
            answerText: 'Answer Text',
            userNotes: 'User Notes',
            orgAdminNote: 'Org Admin Notes',
            bu_title: 'Update Title',
            bu_content: 'Update Content',
            bu_sources: 'Update Sources',
        };

        const headers = [];
        Object.keys(fieldMap).forEach(fieldKey => {
            if (fields[fieldKey]) {
                headers.push(fieldMap[fieldKey]);
            }
        });

        const rows = [headers.map(h => `"${h}"`).join(',')];

        itemsToExport.forEach(item => {
            const rowData = {};

            if (item.dataType === 'question') {
                rowData['ID'] = fields.itemId ? item.id : '';
                rowData['FCFO Name'] = fields.fcfoName ? item.fcfoName : '';
                rowData['Client Name'] = fields.clientName ? item.clientName : '';
                rowData['Date Created'] = fields.dateCreated ? (item.createdAt?.toDate ? item.createdAt.toDate().toISOString() : '') : '';
                rowData['Question Text'] = fields.questionText ? item.questionText : '';
                rowData['Answer Text'] = fields.answerText ? item.answer : '';
                rowData['User Notes'] = fields.userNotes ? item.userNotes : '';
                rowData['Org Admin Notes'] = fields.orgAdminNote ? item.orgAdminNote : '';
            } else if (item.dataType === 'update') {
                rowData['ID'] = fields.itemId ? item.id : '';
                rowData['FCFO Name'] = fields.fcfoName ? item.fcfoName : '';
                rowData['Client Name'] = fields.clientName ? item.clientName : '';
                rowData['Date Created'] = fields.dateCreated ? (item.createdAt?.toDate ? item.createdAt.toDate().toISOString() : '') : '';
                rowData['Update Title'] = fields.bu_title ? item.title : '';
                rowData['Update Content'] = fields.bu_content ? item.content : '';
                rowData['Update Sources'] = fields.bu_sources ? (item.sources?.join('; ') || '') : '';
                rowData['User Notes'] = fields.userNotes ? item.userNotes : '';
                rowData['Org Admin Notes'] = fields.orgAdminNote ? item.orgAdminNote : '';
            }

            const row = headers.map(header => {
                const cell = rowData[header] || '';
                return `"${(cell).toString().replace(/"/g, '""')}"`;
            });
            rows.push(row.join(','));

            // Handle follow-ups
            if (fields.includeFollowUps && item.followUps?.length > 0) {
                item.followUps.forEach(followUp => {
                    const followUpRowData = {
                        'ID': fields.itemId ? followUp.id : '',
                        'FCFO Name': fields.fcfoName ? item.fcfoName : '',
                        'Client Name': fields.clientName ? item.clientName : '',
                        'Date Created': fields.dateCreated ? (followUp.createdAt?.toDate ? followUp.createdAt.toDate().toISOString() : '') : '',
                        'Question Text': fields.questionText ? `[Follow-up] ${followUp.questionText}` : '',
                        'Answer Text': fields.answerText ? followUp.answer : '',
                    };

                    const followUpRow = headers.map(header => {
                        const cell = followUpRowData[header] || '';
                        return `"${(cell).toString().replace(/"/g, '""')}"`;
                    });
                    rows.push(followUpRow.join(','));
                });
            }
        });

        return rows.join('\n');
    };

    // Generate Word document with proper follow-up handling
    const generateWordDocument = async (itemsToExport, fields) => {
        const addField = (sectionChildren, label, value) => {
            if (value && value.toString().trim()) {
                sectionChildren.push(new Paragraph({
                    children: [
                        new TextRun({ text: `${label}: `, bold: true }),
                        new TextRun({ text: value.toString() }),
                    ],
                    spacing: { after: 100 },
                }));
            }
        };

        // NEW: Parse Markdown content into formatted Word paragraphs
        const parseMarkdownContent = (content) => {
            const elements = [];
            if (!content) return elements;

            const lines = content.split('\n');
            let i = 0;

            while (i < lines.length) {
                const line = lines[i];
                const trimmedLine = line.trim();

                // Handle Heading 1
                if (trimmedLine.startsWith('# ') && !trimmedLine.startsWith('## ')) {
                    elements.push(new Paragraph({
                        text: trimmedLine.substring(2).trim(),
                        heading: HeadingLevel.HEADING_1,
                        spacing: { before: 300, after: 150 }
                    }));
                    i++;
                }
                // Handle Heading 2
                else if (trimmedLine.startsWith('## ') && !trimmedLine.startsWith('### ')) {
                    elements.push(new Paragraph({
                        text: trimmedLine.substring(3).trim(),
                        heading: HeadingLevel.HEADING_2,
                        spacing: { before: 250, after: 120 }
                    }));
                    i++;
                }
                // Handle Heading 3
                else if (trimmedLine.startsWith('### ')) {
                    elements.push(new Paragraph({
                        text: trimmedLine.substring(4).trim(),
                        heading: HeadingLevel.HEADING_3,
                        spacing: { before: 200, after: 100 }
                    }));
                    i++;
                }
                // Handle Horizontal Rule
                else if (trimmedLine === '---') {
                    elements.push(new Paragraph({
                        text: '________________________________',
                        spacing: { before: 200, after: 200 }
                    }));
                    i++;
                }
                   // Handle bullets (any indent level) e.g. "- item" with optional leading spaces
                else if (/^\s*-\s/.test(line)) {
                    const leadingSpaces = line.search(/\S/);
                    const indentLevel = Math.max(0, Math.floor(leadingSpaces / 2));
                    const bulletText = line.replace(/^\s*-\s/, '').trim();

                    const textRuns = [new TextRun({ text: '• ' })];
                    const formatRegex = /(\*\*.*?\*\*|\*.*?\*)/g;
                    const parts = bulletText.split(formatRegex).filter(p => p);

                    parts.forEach(part => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            textRuns.push(new TextRun({ text: part.slice(2, -2), bold: true }));
                        } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                            textRuns.push(new TextRun({ text: part.slice(1, -1), italics: true }));
                        } else {
                            textRuns.push(new TextRun({ text: part }));
                        }
                    });

                    elements.push(new Paragraph({
                        children: textRuns,
                        spacing: { after: 50 },
                        indent: { left: 360 + (indentLevel * 360) }
                    }));
                    i++;
                }
                // Handle numbered lists (any indent level) e.g. "  1. item"
                else if (/^\s*\d+\.\s/.test(line)) {
                    const leadingSpaces = line.search(/\S/);
                    const indentLevel = Math.max(0, Math.floor(leadingSpaces / 2));
                    const match = line.match(/^\s*(\d+)\.\s(.+)$/);
                    if (match) {
                        const number = match[1];
                        const numberText = match[2].trim();

                        const textRuns = [new TextRun({ text: `${number}. ` })];
                        const formatRegex = /(\*\*.*?\*\*|\*.*?\*)/g;
                        const parts = numberText.split(formatRegex).filter(p => p);

                        parts.forEach(part => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                                textRuns.push(new TextRun({ text: part.slice(2, -2), bold: true }));
                            } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
                                textRuns.push(new TextRun({ text: part.slice(1, -1), italics: true }));
                            } else {
                                textRuns.push(new TextRun({ text: part }));
                            }
                        });

                        elements.push(new Paragraph({
                            children: textRuns,
                            spacing: { after: 50 },
                            indent: { left: 360 + (indentLevel * 360) }
                        }));
                    }
                    i++;
                }
                // Handle GitHub-style Markdown tables
                else if (/\|/.test(trimmedLine) && lines[i + 1] && /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(lines[i + 1])) {
                    // Collect table lines until a blank line or a non-table line
                    const tableLines = [];
                    // Header row
                    tableLines.push(lines[i].trim());
                    i++;
                    // Separator row (skip but use to confirm it's a table)
                    const separator = lines[i].trim();
                    i++;
                    // Data rows
                    while (i < lines.length && lines[i].trim() && /\|/.test(lines[i])) {
                        tableLines.push(lines[i].trim());
                        i++;
                    }

                    const headerCells = tableLines[0].split('|').map(c => c.trim()).filter(c => c !== '');
                    const dataRows = tableLines.slice(1).map(row => row.split('|').map(c => c.trim()).filter(c => c !== ''));

                    // Build docx table
                    const rows = [];

                    // Header
                    rows.push(new TableRow({
                        children: headerCells.map(text => new TableCell({
                            children: [new Paragraph({ text, spacing: { after: 50 } })],
                            shading: { fill: 'F2F2F2' },
                            margins: { top: 100, bottom: 100, left: 100, right: 100 }
                        }))
                    }));

                    // Body
                    dataRows.forEach(cols => {
                        rows.push(new TableRow({
                            children: cols.map(text => new TableCell({
                                children: [new Paragraph({ text, spacing: { after: 50 } })],
                                margins: { top: 100, bottom: 100, left: 100, right: 100 }
                            }))
                        }));
                    });

                    elements.push(new Table({
                        rows,
                        width: { size: 100, type: WidthType.PERCENTAGE }
                    }));
                }
                // Handle paragraphs with bold or italic
                else if (trimmedLine && (trimmedLine.includes('**') || trimmedLine.includes('*'))) {
                    const textRuns = [];
                    const formatRegex = /(\*\*.*?\*\*|\*[^*]+?\*)/g;
                    const parts = line.split(formatRegex).filter(p => p);
                    
                    if (parts.length === 0 && trimmedLine) {
                        textRuns.push(new TextRun({ text: line }));
                    } else {
                        parts.forEach(part => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                                textRuns.push(new TextRun({ text: part.slice(2, -2), bold: true }));
                            } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2 && !part.includes('**')) {
                                textRuns.push(new TextRun({ text: part.slice(1, -1), italics: true }));
                            } else {
                                textRuns.push(new TextRun({ text: part }));
                            }
                        });
                    }

                    elements.push(new Paragraph({
                        children: textRuns,
                        spacing: { after: 100 }
                    }));
                    i++;
                }
                // Handle regular paragraphs
                else if (trimmedLine) {
                    elements.push(new Paragraph({
                        text: line,
                        spacing: { after: 100 }
                    }));
                    i++;
                }
                // Skip empty lines
                else {
                    i++;
                }
            }

            return elements;
        };

        // NEW: Add a labeled content field with Markdown parsing
        const addMarkdownField = (sectionChildren, label, content) => {
            if (content && content.toString().trim()) {
                sectionChildren.push(new Paragraph({
                    children: [new TextRun({ text: `${label}:`, bold: true })],
                    spacing: { before: 100, after: 50 }
                }));
                const formattedContent = parseMarkdownContent(content.toString());
                sectionChildren.push(...formattedContent);
            }
        };

        const renderFollowUpsForWord = (sectionChildren, followUps) => {
            if (fields.includeFollowUps && followUps?.length > 0) {
                sectionChildren.push(new Paragraph({
                    text: "Follow-up Thread:",
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 200, after: 100 },
                }));

                followUps.forEach((followUp, followUpIndex) => {
                    addField(sectionChildren, `  Follow-up ${followUpIndex + 1}`, followUp.questionText);
                    if (followUp.answer) {
                        addMarkdownField(sectionChildren, "    Answer", followUp.answer);
                    }
                });
            }
        };

        const doc = new Document({
            sections: [{
                children: [
                    new Paragraph({
                        text: "Savvai Firm Portal Export",
                        heading: HeadingLevel.TITLE,
                        spacing: { after: 200 },
                    }),
                    new Paragraph({
                        text: `Export Date: ${new Date().toLocaleDateString()}`,
                        spacing: { after: 400 },
                    }),
                    new Paragraph({
                        text: `Total Items: ${itemsToExport.length}`,
                        spacing: { after: 400 },
                    }),
                ],
            }],
        });

        let questionCount = 0;
        let updateCount = 0;

        itemsToExport.forEach((item) => {
            const itemChildren = [];

            if (item.dataType === 'question') {
                questionCount++;
                itemChildren.push(new Paragraph({
                    text: `Question ${questionCount}`,
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 100 },
                }));

                if (fields.itemId) addField(itemChildren, "Question ID", item.id);
                if (fields.fcfoName) addField(itemChildren, "Advisor", item.fcfoName);
                if (fields.clientName) addField(itemChildren, "Client", item.clientName);
                if (fields.dateCreated) addField(itemChildren, "Created", item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'N/A');
                if (fields.questionText) addField(itemChildren, "Question", item.questionText);
                if (fields.answerText && item.answer) {
                    addMarkdownField(itemChildren, "Answer", item.answer);
                }
                if (item.sources && item.sources.length > 0) {
                    addField(itemChildren, "Sources", item.sources.join('\n'));
                }
                if (fields.userNotes) addField(itemChildren, "User Notes", item.userNotes);
                if (fields.orgAdminNote) addField(itemChildren, "Org Admin Notes", item.orgAdminNote);

                renderFollowUpsForWord(itemChildren, item.followUps);

            } else if (item.dataType === 'update') {
                updateCount++;
                itemChildren.push(new Paragraph({
                    text: `SavvaiSignals ${updateCount}`,
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 300, after: 100 },
                }));

                if (fields.itemId) addField(itemChildren, "Update ID", item.id);
                if (fields.fcfoName) addField(itemChildren, "Advisor", item.fcfoName);
                if (fields.clientName) addField(itemChildren, "Client", item.clientName);
                if (fields.dateCreated) addField(itemChildren, "Created", item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'N/A');
                if (fields.bu_title) addField(itemChildren, "Title", item.title);
                if (fields.bu_content && item.content) {
                    addMarkdownField(itemChildren, "Content", item.content);
                }
                if (fields.bu_sources && item.sources?.length > 0) {
                    addField(itemChildren, "Sources", item.sources.join('\n'));
                }
                if (fields.userNotes) addField(itemChildren, "User Notes", item.userNotes);
                if (fields.orgAdminNote) addField(itemChildren, "Org Admin Notes", item.orgAdminNote);

                renderFollowUpsForWord(itemChildren, item.followUps);
            }

            doc.addSection({ children: itemChildren });
        });

        return await Packer.toBlob(doc);
    };

    // Handle export
    const handleExport = async () => {
        if (!processedItems.length) {
            toast.error('No items to export');
            return;
        }

        try {
            setIsExporting(true);
            const filenameBase = `Savvai-Firm-Export-${new Date().toISOString().split('T')[0]}`;
            let blob;
            let filename;

            if (exportFormat === 'csv') {
                const csvContent = generateCSVContent(processedItems, exportFields);
                blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                filename = `${filenameBase}.csv`;
            } else if (exportFormat === 'word') {
                blob = await generateWordDocument(processedItems, exportFields);
                filename = `${filenameBase}.docx`;
            }

            if (blob) {
                const link = document.createElement('a');
                const url = window.URL.createObjectURL(blob);
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(`${exportFormat.toUpperCase()} exported successfully!`);
            }

            setExportModalOpen(false);
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // Handle single-item export to Word
    const handleSingleItemExport = async (item) => {
        try {
            const filename = `savvai_firm_export_${item.dataType === 'question' ? 'qa' : 'signal'}_${new Date().toISOString().split('T')[0]}.docx`;
            const blob = await generateWordDocument([item], exportFields);
            
            const link = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Export downloaded successfully!');
        } catch (error) {
            console.error('Single item export failed:', error);
            toast.error('Export failed. Please try again.');
        }
    };

    // Render question card
    const renderQuestionCard = (question) => {
        const isExpanded = expandedItems.has(question.id);
        const hasFollowUps = question.followUps && question.followUps.length > 0;
        const isFavoritedByUser = question.favoritedBy && question.favoritedBy.length > 0;
        const hasOrgAdminFeedback = question.orgAdminFeedback && question.orgAdminFeedback.rating;

        return (
            <Card key={question.id} elevation={3} sx={{ mb: 2 }}>
                <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                    {/* Header Section */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box flex={1}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                                <Chip label={`Advisor: ${question.fcfoName}`} size="small" color="primary" variant="outlined" />
                                <Chip label={`Client: ${question.clientName}`} size="small" color="primary" variant="outlined" />
                                {hasFollowUps && (
                                    <Chip icon={<ChatIcon />} label={`${question.followUps.length} follow-up${question.followUps.length !== 1 ? 's' : ''}`} size="small" color="info" variant="outlined" />
                                )}
                                {hasOrgAdminFeedback && (
                                    <Chip icon={<FeedbackIcon />} label={`${question.orgAdminFeedback.rating} star${question.orgAdminFeedback.rating !== 1 ? 's' : ''}`} size="small" color="secondary" variant="outlined" />
                                )}
                                {question.askedViaFirmPortal && (
                                    <Chip icon={<AdminIcon />} label={`Asked by ${question.askedByAdminName || 'Admin'}`} size="small" color="warning" variant="outlined" />
                                )}
                            </Stack>
                            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 500, cursor: 'pointer', mb: 1 }} onClick={() => toggleExpansion(question.id)}>
    {localSearchQuery ? highlightSearchTerms(question.questionText, localSearchQuery) : question.questionText}
</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 14 }} />
                                {formatTimestamp(question.createdAt)}
                            </Typography>
                        </Box>
                        {/* Action Icons in Header */}
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Tooltip title={isFavoritedByUser ? "Favorited by User" : "Not favorited by user"}>
                                <span>
                                    <IconButton size="small" disabled sx={{ color: isFavoritedByUser ? 'warning.main' : 'text.disabled' }}>
                                        {isFavoritedByUser ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Share Thread">
                                <IconButton size="small" onClick={() => handleOpenShareModal(question)}>
                                    <ShareIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Export to Word">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSingleItemExport(question); }}>
                                    <FileDownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                                <IconButton onClick={() => toggleExpansion(question.id)} size="small">
                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Expandable Content */}
                    <Collapse in={isExpanded} timeout="auto">
                        <Divider sx={{ mb: 2 }} />
                        {question.answer && (
                            <Box mb={2}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>Answer:</Typography>
                                <Paper elevation={0} variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Box sx={{ 
                                        fontSize: '1rem', 
                                        lineHeight: 1.7,
                                        '& p': { margin: 0, marginBottom: '0.5em' },
                                        '& p:last-child': { marginBottom: 0 },
                                        '& ul, & ol': { marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '1.5em' },
                                        '& li': { marginBottom: '0.25em' },
                                        '& strong': { fontWeight: 600 },
                                        '& table': { width: '100%', borderCollapse: 'collapse', marginTop: '0.5em', marginBottom: '0.5em' },
                                        '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
                                        '& th': { backgroundColor: '#f2f2f2', fontWeight: 600 }
                                    }}>
                                        <ReactMarkdown 
    remarkPlugins={[remarkGfm]}
    components={getHighlightedMarkdownComponents(localSearchQuery)}
>
    {question.answer}
</ReactMarkdown>
                                    </Box>
                                </Paper>
                            </Box>
                        )}
                        
                        {/* User Note Display */}
                        {question.userNotes && (
                            <Box mb={2}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>User Note:</Typography>
                                <Paper elevation={0} variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '1rem' }}>
                                        {question.userNotes}
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                        
                        {/* Sources Display */}
                        {question.sources && question.sources.length > 0 && (
                            <Box mb={2}>
                                <Box
                                    onClick={() => toggleSourcesExpansion(question.id)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        mb: 1,
                                        '&:hover': {
                                            opacity: 0.7
                                        }
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '1.1rem', mr: 1 }}>
                                        Sources:
                                    </Typography>
                                    <ExpandMoreIcon
                                        sx={{
                                            transform: expandedSources.has(question.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s'
                                        }}
                                    />
                                </Box>
                                <Collapse in={expandedSources.has(question.id)} timeout="auto">
                                    <Paper elevation={0} variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                        <Stack spacing={1}>
                                            {question.sources.map((source, index) => (
                                                <Typography key={index} variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                    <a href={source} target="_blank" rel="noopener noreferrer">
                                                        {source}
                                                    </a>
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Paper>
                                </Collapse>
                            </Box>
                        )}

                        {/* Org Admin Note Display & Edit */}
                        <Box mb={2}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>Org Admin Note:</Typography>
                            <Paper elevation={0} variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                {question.orgAdminNote ? (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '1rem' }}>
                                        {question.orgAdminNote}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No org admin note added yet.
                                    </Typography>
                                )}
                                <Button size="small" startIcon={<EditNoteIcon />} onClick={() => handleOpenNoteModal(question)} sx={{ mt: 1 }}>
                                    {question.orgAdminNote ? 'Edit Note' : 'Add Note'}
                                </Button>
                            </Paper>
                        </Box>

                        {hasFollowUps && renderFollowUps(question.followUps)}
                        
                         {/* Action Buttons */}
                        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                            <Button 
                                variant="outlined" 
                                size="small" 
                                startIcon={<FeedbackIcon />}
                                onClick={() => openFeedbackDialog(question)}
                                color={hasOrgAdminFeedback ? 'secondary' : 'primary'}
                            >
                                {hasOrgAdminFeedback ? 'Update Feedback' : 'Leave Feedback'}
                            </Button>
                            {isAdmin && (
                                <>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ChatIcon />}
                                        onClick={() => handleOpenFollowUp(question, 'question')}
                                    >
                                        Ask Follow-up
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={memoGenerating === question.id ? <CircularProgress size={16} /> : <ArticleIcon />}
                                        onClick={() => handleGenerateDecisionMemo(question, 'question')}
                                        disabled={memoGenerating === question.id}
                                    >
                                        {memoGenerating === question.id ? 'Generating...' : 'Decision Memo'}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>
        );
    };

    // Render business update card
    const renderBusinessUpdateCard = (update) => {
        const isExpanded = expandedItems.has(update.id);
        const hasFollowUps = update.followUps && update.followUps.length > 0;
        const isFavoritedByUser = update.favoritedBy && update.favoritedBy.length > 0;
        const hasOrgAdminFeedback = update.orgAdminFeedback && update.orgAdminFeedback.rating;

        return (
            <Card key={update.id} elevation={3} sx={{ mb: 2, border: '1px solid', borderColor: 'secondary.light', backgroundColor: '#f3e5f5' }}>
                <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box flex={1}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                                <Chip icon={<ArticleIcon />} label="SavvaiSignals" size="small" color="secondary" variant="filled" sx={{ fontWeight: 'bold' }} />
                                <Chip label={`Advisor: ${update.fcfoName}`} size="small" color="secondary" variant="outlined" />
                                <Chip label={`Client: ${update.clientName}`} size="small" color="secondary" variant="outlined" />
                                {hasFollowUps && (
                                    <Chip icon={<ChatIcon />} label={`${update.followUps.length} follow-up${update.followUps.length !== 1 ? 's' : ''}`} size="small" color="info" variant="outlined" />
                                )}
                                {hasOrgAdminFeedback && (
                                    <Chip icon={<FeedbackIcon />} label={`${update.orgAdminFeedback.rating} star${update.orgAdminFeedback.rating !== 1 ? 's' : ''}`} size="small" color="secondary" variant="outlined" />
                                )}
                            </Stack>
                            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600, cursor: 'pointer', mb: 1, color: 'secondary.dark' }} onClick={() => toggleExpansion(update.id)}>
    {localSearchQuery ? highlightSearchTerms(update.title || 'SavvaiSignals', localSearchQuery) : (update.title || 'SavvaiSignals')}
</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 14 }} />
                                {formatTimestamp(update.createdAt)}
                            </Typography>
                        </Box>
                        {/* Action Icons in Header */}
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Tooltip title={isFavoritedByUser ? "Favorited by User" : "Not favorited by user"}>
                                <span>
                                    <IconButton size="small" disabled sx={{ color: isFavoritedByUser ? 'warning.main' : 'text.disabled' }}>
                                        {isFavoritedByUser ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Share SavvaiSignals">
                                <IconButton size="small" onClick={() => handleOpenShareModal(update)}>
                                    <ShareIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Export to Word">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSingleItemExport(update); }}>
                                    <FileDownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                                <IconButton onClick={() => toggleExpansion(update.id)} size="small">
                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Collapse in={isExpanded} timeout="auto">
                        <Divider sx={{ mb: 2 }} />
                        <Box mb={2}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>Update Details:</Typography>
                            <Paper elevation={0} variant="outlined" sx={{ p: 2, backgroundColor: 'white' }}>
                                <Box sx={{ 
                                    fontSize: '1rem', 
                                    lineHeight: 1.7,
                                    '& p': { margin: 0, marginBottom: '0.5em' },
                                    '& p:last-child': { marginBottom: 0 },
                                    '& ul, & ol': { marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '1.5em' },
                                    '& li': { marginBottom: '0.25em' },
                                    '& strong': { fontWeight: 600 },
                                    '& h1, & h2, & h3': { marginTop: '1em', marginBottom: '0.5em' },
                                    '& table': { width: '100%', borderCollapse: 'collapse', marginTop: '0.5em', marginBottom: '0.5em' },
                                    '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
                                    '& th': { backgroundColor: '#f2f2f2', fontWeight: 600 }
                                }}>
                                    <ReactMarkdown 
    remarkPlugins={[remarkGfm]}
    components={getHighlightedMarkdownComponents(localSearchQuery)}
>
    {update.content || 'No content available.'}
</ReactMarkdown>
                                </Box>
                            </Paper>
                        </Box>
                        
                        {/* Sources Display */}
                        {update.sources && update.sources.length > 0 && (
                            <Box mb={2}>
                                <Box
                                    onClick={() => toggleSourcesExpansion(update.id)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        mb: 1,
                                        '&:hover': {
                                            opacity: 0.7
                                        }
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '1.1rem', mr: 1 }}>
                                        Sources:
                                    </Typography>
                                    <ExpandMoreIcon
                                        sx={{
                                            transform: expandedSources.has(update.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s'
                                        }}
                                    />
                                </Box>
                                <Collapse in={expandedSources.has(update.id)} timeout="auto">
                                    <Paper elevation={0} variant="outlined" sx={{ p: 2, backgroundColor: 'white' }}>
                                        <Stack spacing={1}>
                                            {update.sources.map((source, index) => (
                                                <Typography key={index} variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                    <a href={source} target="_blank" rel="noopener noreferrer">
                                                        {source}
                                                    </a>
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Paper>
                                </Collapse>
                            </Box>
                        )}

                        {/* User Note Display */}
                        {update.userNotes && (
                            <Box mb={2}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>User Note:</Typography>
                                <Paper elevation={0} variant="outlined" sx={{ p: 2, backgroundColor: 'white' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '1rem' }}>
                                        {update.userNotes}
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                        
                        {/* Org Admin Note Display & Edit */}
                        <Box mb={2}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ fontSize: '1.1rem' }}>Org Admin Note:</Typography>
                            <Paper elevation={0} variant="outlined" sx={{ p: 2, backgroundColor: 'white' }}>
                                {update.orgAdminNote ? (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '1rem' }}>
                                        {update.orgAdminNote}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No org admin note added yet.
                                    </Typography>
                                )}
                                <Button size="small" startIcon={<EditNoteIcon />} onClick={() => handleOpenNoteModal(update)} sx={{ mt: 1 }}>
                                    {update.orgAdminNote ? 'Edit Note' : 'Add Note'}
                                </Button>
                            </Paper>
                        </Box>

                        {hasFollowUps && renderFollowUps(update.followUps)}

                         {/* Action Buttons */}
                        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                            <Button 
                                variant="outlined" 
                                size="small" 
                                startIcon={<FeedbackIcon />}
                                onClick={() => openFeedbackDialog(update)}
                                color={hasOrgAdminFeedback ? 'secondary' : 'primary'}
                            >
                                {hasOrgAdminFeedback ? 'Update Feedback' : 'Leave Feedback'}
                            </Button>
                            {isAdmin && (
                                <>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ChatIcon />}
                                        onClick={() => handleOpenFollowUp(update, 'business_update')}
                                    >
                                        Ask Follow-up
                                    </Button>
                                    {/* Per-Signal Decision Memo Buttons */}
                                    {(() => {
                                        const signalMatches = update.content?.match(/### Signal \d+:[^\n]+/g);
                                        if (!signalMatches || signalMatches.length === 0) {
                                            // Fallback: single button if no signals found
                                            return (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                    startIcon={memoGenerating === update.id ? <CircularProgress size={16} /> : <ArticleIcon />}
                                                    onClick={() => handleGenerateDecisionMemo(update, 'business_update', null)}
                                                    disabled={memoGenerating === update.id}
                                                >
                                                    {memoGenerating === update.id ? 'Generating...' : 'Decision Memo'}
                                                </Button>
                                            );
                                        }
                                        
                                        // Multiple signal buttons
                                        return signalMatches.map((signalTitle, index) => {
                                            const signalNumber = index + 1;
                                            const cleanTitle = signalTitle.replace(/### Signal \d+:\s*/, '').trim();
                                            const buttonId = `${update.id}-signal-${signalNumber}`;
                                            
                                            return (
                                                <Tooltip key={buttonId} title={cleanTitle}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="secondary"
                                                        startIcon={memoGenerating === buttonId ? <CircularProgress size={16} /> : <ArticleIcon />}
                                                                                                        onClick={() => handleGenerateDecisionMemo(update, 'business_update', signalNumber, buttonId)}
                                                        disabled={memoGenerating === buttonId}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {memoGenerating === buttonId ? 'Generating...' : `DM: Signal ${signalNumber}`}
                                                    </Button>
                                                </Tooltip>
                                            );
                                        });
                                    })()}
                                </>
                            )}
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>
        );
    };


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading firm data...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            {/* Filter Controls */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    {/* Row 1: FCFO, Client, Sort, Search */}
                    <Grid size={{ xs: 12, md: 3 }}>
    <FormControl fullWidth size="small">
        <InputLabel>Filter by Advisor</InputLabel>
                            <Select
                                value={selectedFcfoId}
                                label="Filter by FCFO"
                                onChange={(e) => setSelectedFcfoId(e.target.value)}
                            >
                                <MenuItem value="ALL">All Advisors</MenuItem>
                                {members.map(member => (
                                    <MenuItem key={member.id} value={member.id}>
                                        {member.name || member.email}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
    <FormControl fullWidth size="small">
        <InputLabel>Filter by Client</InputLabel>
                            <Select
                                value={selectedClientId}
                                label="Filter by Client"
                                onChange={(e) => setSelectedClientId(e.target.value)}
                            >
                                <MenuItem value="ALL">All Clients</MenuItem>
                                {filteredClients.map(client => (
                                    <MenuItem key={client.id} value={client.id}>
                                        {client.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
    <FormControl fullWidth size="small">
        <InputLabel>Sort by</InputLabel>
                            <Select
                                value={sortOption}
                                label="Sort by"
                                onChange={(e) => setSortOption(e.target.value)}
                            >
                                <MenuItem value="newest">Newest First</MenuItem>
                                <MenuItem value="oldest">Oldest First</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <SearchInput 
                            onSearch={(value) => setLocalSearchQuery(value)} 
                            onClear={() => setLocalSearchQuery('')}
                            initialValue={localSearchQuery}
                        />
                    </Grid>

                    {/* Row 2: Date filters */}
<Grid size={{ xs: 6, md: 2 }}>
    <TextField
        label="Date From"
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, md: 2 }}>
    <TextField
        label="Date To"
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }} />
                </Grid>

                <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                    <Grid>
    {(localSearchQuery || dateFrom || dateTo) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
                {processedItems.length} item{processedItems.length !== 1 ? 's' : ''} found
                {localSearchQuery && ` for "${localSearchQuery}"`}
                {(dateFrom || dateTo) && (
                    <span> • Date range: {dateFrom || 'any'} to {dateTo || 'any'}</span>
                )}
            </Typography>
            {showLoadingNote && (
                <Typography variant="caption" color="info.main" sx={{ fontStyle: 'italic' }}>
                    Loading complete history...
                </Typography>
            )}
        </Box>
    )}
</Grid>
                    <Grid>
                        <Button
                            variant="contained"
                            onClick={() => setExportModalOpen(true)}
                            disabled={processedItems.length === 0}
                        >
                            Configure & Export
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Time-box indicator */}
            {!showAllHistory && !dateFrom && !dateTo && (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2, 
                    p: 1.5, 
                    bgcolor: 'grey.100', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300'
                }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing last 90 days of firm activity
                    </Typography>
                    <Button 
                        size="small" 
                        onClick={() => setShowAllHistory(true)}
                        sx={{ ml: 2 }}
                    >
                        Show All History
                    </Button>
                </Box>
            )}

            {showAllHistory && !dateFrom && !dateTo && (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 2, 
                    p: 1.5, 
                    bgcolor: 'info.50', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'info.200'
                }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing complete firm history
                    </Typography>
                    <Button 
                        size="small" 
                        onClick={() => setShowAllHistory(false)}
                        sx={{ ml: 2 }}
                    >
                        Show Last 90 Days
                    </Button>
                </Box>
            )}

            {/* Feed Display */}
            {processedItems.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.100' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No activity found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Activity from your team will appear here once Advisors start asking questions or receiving signals.
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    {processedItems.map(item => {
                        if (item.dataType === 'question') {
                            return renderQuestionCard(item);
                        } else if (item.dataType === 'update') {
                            return renderBusinessUpdateCard(item);
                        }
                        return null;
                    })}
                </Box>
            )}
            {/* Share Modal */}
            {selectedItem && (
                <ShareModal
                    open={shareModalOpen}
                    onClose={() => setShareModalOpen(false)}
                    item={selectedItem}
                    itemType={selectedItem.dataType === 'question' ? 'question' : 'update'}
                    followUps={selectedItem.followUps || []}
                    onShare={handleShare}
                    user={user}
                />
            )}

            {/* Note Modal */}
            <Dialog open={noteModalOpen} onClose={() => setNoteModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>
                    Org Admin Note for {selectedItem?.dataType === 'question' ? 'Question' : 'SavvaiSignals'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Note"
                        type="text"
                        fullWidth
                        multiline
                        rows={6}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        variant="outlined"
                        placeholder="Add a private note for your firm's administrators..."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNoteModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveNote} variant="contained">Save Note</Button>
                </DialogActions>
            </Dialog>

            {/* Feedback Dialog */}
            <Dialog open={feedbackDialogOpen} onClose={closeFeedbackDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedItemForFeedback?.orgAdminFeedback ? 'Update Feedback' : 'Leave Feedback'}
                    {selectedItemForFeedback && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Regarding: "{(selectedItemForFeedback.questionText || selectedItemForFeedback.title)?.substring(0, 100)}..."
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            How would you rate this {selectedItemForFeedback?.dataType === 'update' ? 'update' : 'answer'}?
                        </Typography>
                        <Rating
                            value={feedbackRating}
                            onChange={(event, newValue) => setFeedbackRating(newValue)}
                            size="large"
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            multiline
                            rows={3}
                            fullWidth
                            label="Additional comments (optional)"
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Share your thoughts about this content..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFeedbackDialog}>Cancel</Button>
                    <Button
                        onClick={handleFeedbackSubmit}
                        variant="contained"
                        disabled={feedbackRating === 0 || submittingFeedback}
                    >
                        {submittingFeedback ? <CircularProgress size={20} /> : 'Submit Feedback'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Export Configuration Modal */}
            <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Configure Export</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
                        <InputLabel>Export Format</InputLabel>
                        <Select
                            value={exportFormat}
                            label="Export Format"
                            onChange={(e) => setExportFormat(e.target.value)}
                        >
                            <MenuItem value="csv">CSV (.csv)</MenuItem>
                            <MenuItem value="word">Word Document (.docx)</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="subtitle2" gutterBottom>Select Fields to Include:</Typography>
                    <Grid container spacing={1}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel control={<Checkbox checked={exportFields.itemId} onChange={(e) => setExportFields(prev => ({...prev, itemId: e.target.checked}))} />} label="Item ID" />
                            <FormControlLabel control={<Checkbox checked={exportFields.fcfoName} onChange={(e) => setExportFields(prev => ({...prev, fcfoName: e.target.checked}))} />} label="Advisor Name" />
                            <FormControlLabel control={<Checkbox checked={exportFields.clientName} onChange={(e) => setExportFields(prev => ({...prev, clientName: e.target.checked}))} />} label="Client Name" />
                            <FormControlLabel control={<Checkbox checked={exportFields.dateCreated} onChange={(e) => setExportFields(prev => ({...prev, dateCreated: e.target.checked}))} />} label="Date Created" />
                            <FormControlLabel control={<Checkbox checked={exportFields.questionText} onChange={(e) => setExportFields(prev => ({...prev, questionText: e.target.checked}))} />} label="Q&A - Question" />
                            <FormControlLabel control={<Checkbox checked={exportFields.answerText} onChange={(e) => setExportFields(prev => ({...prev, answerText: e.target.checked}))} />} label="Q&A - Answer" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel control={<Checkbox checked={exportFields.bu_title} onChange={(e) => setExportFields(prev => ({...prev, bu_title: e.target.checked}))} />} label="Signal - Title" />
                            <FormControlLabel control={<Checkbox checked={exportFields.bu_content} onChange={(e) => setExportFields(prev => ({...prev, bu_content: e.target.checked}))} />} label="Signal - Content" />
                            <FormControlLabel control={<Checkbox checked={exportFields.bu_sources} onChange={(e) => setExportFields(prev => ({...prev, bu_sources: e.target.checked}))} />} label="Signal - Sources" />
                            <FormControlLabel control={<Checkbox checked={exportFields.userNotes} onChange={(e) => setExportFields(prev => ({...prev, userNotes: e.target.checked}))} />} label="User Notes" />
                            <FormControlLabel control={<Checkbox checked={exportFields.orgAdminNote} onChange={(e) => setExportFields(prev => ({...prev, orgAdminNote: e.target.checked}))} />} label="Org Admin Note" />
                            <FormControlLabel control={<Checkbox checked={exportFields.includeFollowUps} onChange={(e) => setExportFields(prev => ({...prev, includeFollowUps: e.target.checked}))} />} label="Include Follow-ups" />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleExport} variant="contained" disabled={isExporting}>
                        {isExporting ? <CircularProgress size={24} /> : `Export to ${exportFormat.toUpperCase()}`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Follow-up Question Modal */}
            <Dialog 
                open={followUpModalOpen} 
                onClose={() => !followUpSubmitting && setFollowUpModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <ChatIcon color="primary" />
                        <span>Ask Follow-up Question</span>
                    </Stack>
                    {followUpParent?.item && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Following up on: {followUpParent.type === 'question' 
                                ? followUpParent.item.questionText?.substring(0, 100) 
                                : followUpParent.item.title}
                            {((followUpParent.type === 'question' ? followUpParent.item.questionText : followUpParent.item.title)?.length > 100) && '...'}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        label="Your follow-up question"
                        inputRef={followUpRef}
                        defaultValue=""
                        disabled={followUpSubmitting}
                        placeholder="Ask a follow-up question to dive deeper into this topic..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setFollowUpModalOpen(false)} 
                        disabled={followUpSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitFollowUp}
                        variant="contained"
                        disabled={followUpSubmitting}
                        startIcon={followUpSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
                    >
                        {followUpSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default FirmPortalFeed;